import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { matchingApi } from '@/api';
import {
  Alert,
  BoxList,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
} from '@/components/ui';
import { InternshipCard } from '@/features/internships/InternshipCard';
import { useSaveInternship } from '@/features/internships/useSaveInternship';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/**
 * Roles ranked by fit, with the reasoning attached to each one.
 *
 * Roles the student has already applied to stay in the list, marked with
 * where the application stands ("Interview scheduled", "Joined") and linking
 * to it instead of offering Apply again.
 */
/**
 * The ranking is computed for the whole list at once, so the panel asks for
 * the most the API returns and pages through it here.
 */
const RECOMMENDATION_LIMIT = 50;
const PAGE_SIZE = 10;

/** "For you" as a page of its own: AI features -> Recommendations. */
export function StudentRecommendationsPage() {
  return (
    // On a phone or tablet held upright the card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:flex-1">
      <PageHeader
        title="Recommendations"
        description="Open roles ranked by how well they fit your profile."
        action={
          <LinkButton to={paths.internships} variant="tintBrand">
            <Icon name="search" size={16} />
            Find internships
          </LinkButton>
        }
      />
      <RecommendationsPanel />
    </div>
  );
}

export function RecommendationsPanel() {
  const { toggle, pendingId } = useSaveInternship();
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['recommendations'],
    queryFn: () => matchingApi.getRecommendations(RECOMMENDATION_LIMIT),
  });

  if (isPending) return <LoadingState label="Working out what suits you…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const all = data ?? [];
  const totalPages = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
  // A save can shorten the list; never sit on a page that no longer exists.
  const current = Math.min(page, totalPages);
  const rows = all.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  // Every row scored at zero confidence means the profile, not the listings.
  const profileIsBare = all.length > 0 && all.every((row) => row.match.confidence === 0);
  const advice = all[0]?.match?.missing_from_profile ?? [];

  return (
    <div className="flex flex-1 flex-col">
      {/* The page header says this now that For you is a page of its own.
      <p className="text-ink-muted mb-4 text-sm">
        Open roles ranked by how well they fit your profile.
      </p> */}

      {profileIsBare && (
        <Alert tone="info" className="mb-6" title="We cannot rank these yet">
          There is nothing on your profile to compare against, so this is just the newest roles.
          Fill in a few things and the order starts to mean something.
          {advice.length > 0 && (
            <ul className="mt-2 list-inside list-disc">
              {advice.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          <p className="mt-2">
            Nothing to fill in yet?{' '}
            <Link to={paths.student.career} className="font-medium underline">
              Tell us what you want to do
            </Link>{' '}
            instead — a goal is enough to start ranking on.
          </p>
        </Alert>
      )}

      <ListCard
        fill
        className="flex-1"
        footer={
          all.length > 0 && (
            <Pagination
              page={current}
              pageSize={PAGE_SIZE}
              totalPages={totalPages}
              totalItems={all.length}
              onPageChange={setPage}
            />
          )
        }
        state={
          all.length === 0 ? (
            <EmptyState
              icon={<Icon name="star" size={22} />}
              title="Nothing to suggest right now"
              description="You have applied to everything open that we could find. Check back as new roles are published."
              action={<LinkButton to={paths.internships}>Browse internships</LinkButton>}
            />
          ) : null
        }
      >
        <BoxList>
          {rows.map((row) => (
            <li key={row.internship.id}>
              <InternshipCard
                internship={row.internship}
                match={row.match}
                application={row.application}
                onToggleSave={toggle}
                isSaving={pendingId === row.internship.id}
              />
            </li>
          ))}
        </BoxList>
      </ListCard>
    </div>
  );
}
